'use client';

import { useState, useEffect } from 'react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { useTheme } from 'next-themes';
import { useRouter, usePathname } from 'next/navigation';

// Dynamically import ThemeToggle to reduce initial bundle size
const ThemeToggle = dynamic(() => import('./ThemeToggle'), { 
  ssr: false,
  loading: () => <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
});

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [activeTab, setActiveTab] = useState('Home');
  const { theme } = useTheme();
  const router = useRouter();
  const pathname = usePathname();

  const menuItems = ["Home", "Work", "Resources", "About"];

  const scrollToSection = (section: string) => {
    requestAnimationFrame(() => {
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    });
  };

  const handleMenuClick = (item: string) => {
    setActiveTab(item);
    if (item === 'UX Psychology') {
      router.push('/blog');
    } else if (item === 'Home') {
      router.push('/');
    } else {
      scrollToSection(item);
    }
  };

  const handleMobileClick = (item: string) => {
    setActiveTab(item);
    if (item === 'UX Psychology') {
      router.push('/blog');
      setMobileMenuOpen(false);
    } else if (item === 'Home') {
      router.push('/');
      setMobileMenuOpen(false);
    } else {
      scrollToSection(item);
      setMobileMenuOpen(false);
    }
  };
  
  // Set active tab based on pathname
  useEffect(() => {
    if (pathname === '/blog') {
      setActiveTab('UX Psychology');
    } else if (pathname === '/') {
      setActiveTab('Home');
    }
  }, [pathname]);

  useEffect(() => {
    setMounted(true);
    
    let ticking = false;
    let lastScrollY = 0;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          // Only update state if scroll position changed significantly
          if (Math.abs(currentScrollY - lastScrollY) > 20) { // Increased threshold
            setIsScrolled(currentScrollY > 50);
            lastScrollY = currentScrollY;
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    
    // Throttle scroll events more aggressively
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 will-change-transform ${
        isScrolled
          ? ' '
          : 'bg-transparent'
      }`}
    >
      <nav className="flex max-w-7xl w-full px-4 sm:px-6 lg:px-16 xl:max-w-6xl mx-auto items-center justify-between py-5">
        {/* Logo */}
        <Link 
          href="/" 
          className={`flex items-center space-x-2 transition-all duration-500 ${
            isScrolled ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
          >
          {!mounted ? (
            <div className="w-[100px] h-[30px] bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          ) : (
                <Image
                  src={theme === 'dark' ? '/images/logo-dark.svg' : '/images/logo-white.svg'}
                  alt="Logo"
                  width={130}
                  height={40}
                  priority
                  fetchPriority="high"
                />
          )}
        </Link>

        {/* Center pill-shaped navigation */}
        <div
          className={`hidden lg:flex items-center justify-center gap-8 px-4 py-3 rounded-full transition-all duration-300 ${
            isScrolled
              ? 'bg-[#E5DAD1] dark:bg-neutral-700 shadow-lg'
              : 'bg-[#E5DAD1] dark:bg-neutral-700 shadow-[0_4px_20px_rgba(0,0,0,0.05)]'
          }`}
        >
          {menuItems.map((item, idx) => (
            <button
              key={idx}
              onClick={() => handleMenuClick(item)}
              className={`font-medium transition-all ${
                item === activeTab
                  ? 'bg-[#4F4040] dark:bg-white text-white dark:text-black px-4 py-1 rounded-full '
                  : 'text-black dark:text-white hover:text-gray-600 dark:hover:text-gray-300'
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        {/* Right side: Theme */}
        <div 
         className={`hidden lg:flex items-center justify-end transition-all duration-500 ${
         isScrolled ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}>
          <ThemeToggle />
        </div>

        {/* Mobile menu toggle */}
        <div className="flex lg:hidden">
          {/* <ThemeToggle /> */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="p-2"
            aria-label="Open navigation menu"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <span className="sr-only">Open navigation menu</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden">
          <div className="fixed inset-0 z-50 bg-[#E5DAD1] dark:bg-[#2D3033] h-screen w-screen">
            <div className="flex flex-col h-screen w-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-[#E5DAD1] dark:bg-[#2D3033] flex-shrink-0">
                <Link href="/" className="py-5 px-5">
                  {!mounted ? (
                    <div className="w-[100px] h-[30px] bg-gray-200 dark:bg-gray-700 rounded animate-pulse" aria-hidden="true" />
                  ) : (
                    <Image
                      src={theme === 'dark' ? '/images/logo-dark.svg' : '/images/logo-white.svg'}
                      alt="Logo"
                      width={120}
                      height={40}
                      priority
                      fetchPriority="high"
                    />
                  )}
                </Link>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-md p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  aria-label="Close navigation menu"
                >
                  <span className="sr-only">Close menu</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              {/* Menu Items - Full Height */}
              <div className="flex-1 flex items-center justify-center bg-[#E5DAD1] dark:bg-[#2D3033] min-h-0">
                <nav className="space-y-10 w-full max-w-lg px-8">
                  {menuItems.map((item) => (
                    <button
                      key={item}
                      className={`block w-full text-center px-8 py-8 text-lg font-bold rounded-2xl transition-all duration-300 transform hover:scale-105 active:scale-95 ${
                        item === activeTab
                          ? 'bg-neutral-700 dark:bg-white text-white dark:text-black'
                          : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                      onClick={() => handleMobileClick(item)}
                    >
                      {item}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
      
    </header>
  );
}
